// Phase 2Z.o — Cycle-week chip (Option α: display-only V1).
//
// Pure JS, deterministic, byte-for-byte equivalent to the Python
// engine's `_rsb_dynamic_workday_shift` cycle-index derivation:
//
//   cycle_idx       = (target_d - anchor_d).days % cycle_length_days
//   week_in_cycle   = cycle_idx // 7
//   total_weeks     = ceil(cycle_length_days / 7)
//
// V1 is DISPLAY-ONLY. No rotation is simulated, no engine assumption
// is made — the chip just shows which cycle-week the next-Monday
// generate would seed. This is the safest foundation step before
// any rotation logic is added later.

function _parseISODate(iso) {
  // iso is "YYYY-MM-DD" — parse as a UTC date so we don't drift by
  // an hour across daylight saving boundaries when subtracting.
  if (!iso || typeof iso !== "string") return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

function _toUTCMidnight(d) {
  return new Date(Date.UTC(
    d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
  ));
}

// Returns the next Monday on/after `from` as a Date (UTC midnight).
// Used for V1's default target ("Generate now would seed this week").
export function nextMondayUTC(from) {
  const base = from ? _toUTCMidnight(new Date(from)) : _toUTCMidnight(new Date());
  // Monday = 1 in JS getUTCDay() (Sunday=0).
  const dow = base.getUTCDay();
  const daysToMon = dow === 1 ? 0 : ((1 - dow + 7) % 7) || 7;
  const next = new Date(base);
  next.setUTCDate(next.getUTCDate() + daysToMon);
  return next;
}

export function isoUTC(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Pure cycle-week math. Returns null if inputs are malformed (caller
// renders nothing in that case — never crashes the page).
export function computeCycleWeek({ profile, targetWeekStart }) {
  if (!profile) return null;
  const cycleLen = Number(profile.cycle_length_days || 0);
  if (!cycleLen || cycleLen < 1) return null;
  const anchor = _parseISODate(profile.cycle_anchor_date);
  if (!anchor) return null;
  const target = targetWeekStart instanceof Date
    ? _toUTCMidnight(targetWeekStart)
    : _parseISODate(targetWeekStart) || nextMondayUTC();
  const totalWeeks = Math.ceil(cycleLen / 7);
  const delta = Math.floor(
    (target.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24),
  );
  // `cycle_idx` mirrors the engine: day-index within the cycle.
  // The Python engine uses `delta_days % cycle_len` for the day-level
  // cycle_idx; for the WEEK-level chip we want the same value but
  // floor-divided by 7.
  let cycleIdx = ((delta % cycleLen) + cycleLen) % cycleLen;
  const weekInCycle = Math.floor(cycleIdx / 7);
  return {
    cycleLengthDays: cycleLen,
    totalWeeks,
    weekInCycle,                      // 0-indexed
    weekLabel: `Week ${weekInCycle + 1} of ${totalWeeks}`,
    targetWeekStartISO: isoUTC(target),
    isMultiWeek: totalWeeks > 1,
  };
}
